// Bulk update from a spreadsheet — preview first, then apply
// (backend: routes/imports.py → app/bulk_import.py for companies,
// app/bulk_import_records.py for contacts and deals).
//
// Each row finds its record by ID (the number on every record page), else by
// the kind's fallback keys — never a guess: two candidates is "ambiguous" and
// the row is skipped. A blank cell leaves a field alone; columns the importer
// doesn't know are ignored, so an Atlas export imports as is. Applying a row
// is the record's own edit (pins, audit, owner cascade); a company's
// "Acquired" files the acquisition, exits live deals as Company sold and
// writes the note. DealCloud is left alone unless ticked.

const { useState } = React;

// kind → copy, the fields a preview can change ([field, label, key in `set`]),
// and where a matched record lives.
const IMPORT_KINDS = {
  companies: {
    title: 'Import companies', noun: ['company', 'companies'], path: 'companies',
    columns: 'ID · Company · Website · Acquired · Acquired by · Announcement link · DNC · Owner · Industry · Revenue · EBITDA · City · State · Country',
    fields: [
      ['is_acquired', 'Acquired', 'is_acquired'], ['is_dnc', 'DNC', 'is_dnc'], ['owner', 'Owner', 'assigned_user_id'],
      ['industry', 'Industry', 'industry_id'], ['website', 'Website', 'website'], ['revenue_est', 'Revenue', 'revenue_est'],
      ['ebitda_est', 'EBITDA', 'ebitda_est'], ['hq_city', 'HQ city', 'hq_city'], ['hq_state', 'HQ state', 'hq_state'],
      ['country', 'Country', 'country'],
    ],
  },
  contacts: {
    title: 'Import people', noun: ['person', 'people'], path: 'people',
    columns: 'ID · Email · First name · Last name · Company · Title · Phone · Owner · Contact type · Decision maker · LinkedIn · City · State · Country',
    fields: [
      ['title', 'Title', 'title'], ['phone', 'Phone', 'phone'], ['owner', 'Owner', 'contact_owner_id'],
      ['contact_type', 'Contact type', 'contact_type'], ['is_decision_maker', 'Decision maker', 'is_decision_maker'],
      ['linkedin_url', 'LinkedIn', 'linkedin_url'], ['city', 'City', 'city'], ['state', 'State', 'state'],
      ['country', 'Country', 'country'],
    ],
  },
  deals: {
    title: 'Import deals', noun: ['deal', 'deals'], path: 'deals',
    columns: 'ID · Engagement · Company · Dealmaker · Associate · On hold · Hot score · Est close date · Est fees · Revenue · EBITDA · Next action · Next action date',
    fields: [
      ['dealmaker', 'Dealmaker', 'dealmaker_id'], ['associate', 'Associate', 'associate_id'], ['on_hold', 'On hold', 'on_hold'],
      ['hot_score', 'Hot score', 'hot_score'], ['estimated_close_date', 'Est. close date', 'estimated_close_date'],
      ['estimated_fees', 'Est. fees', 'estimated_fees'], ['reported_revenue', 'Revenue', 'reported_revenue'],
      ['reported_ebitda', 'EBITDA', 'reported_ebitda'], ['next_action_notes', 'Next action', 'next_action_notes'],
      ['next_action_date', 'Next action date', 'next_action_date'],
    ],
  },
};
const IMPORT_CHUNK = 100;   // routes/imports.py APPLY_CHUNK
const MONEY_FIELDS = new Set(['revenue_est', 'ebitda_est', 'estimated_fees', 'reported_revenue', 'reported_ebitda']);

const fmtImportValue = (field, v) => {
  if (v === null || v === undefined || v === '') return '—';
  if (MONEY_FIELDS.has(field)) return window.fmtMoney ? window.fmtMoney(v) : String(v);
  if (typeof v === 'boolean') return v ? 'Yes' : 'No';
  return String(v);
};

const STATUS_META = {
  update:    { label: 'update',    hue: 'blue' },
  no_change: { label: 'no change', hue: 'slate' },
  not_found: { label: 'not found', hue: 'amber' },
  ambiguous: { label: 'ambiguous', hue: 'amber' },
};

// A preview row's record, whatever the kind (companies carry company_*).
const recUuid = (r) => r.record_uuid || r.company_id;
const recName = (r) => r.record_name || r.company_name;

const ImportDrawer = ({ kind = 'companies', open, onClose, onDone }) => {
  const cfg = IMPORT_KINDS[kind];
  const [one, many] = cfg.noun;
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [off, setOff] = useState(() => new Set());   // fields left out of the apply
  const [pushDc, setPushDc] = useState(false);
  const [progress, setProgress] = useState(null);    // {done, total}
  const [results, setResults] = useState(null);
  const [filter, setFilter] = useState('update');
  const fileRef = React.useRef(null);

  const reset = () => {
    setPreview(null); setError(null); setOff(new Set()); setPushDc(false);
    setProgress(null); setResults(null); setFilter('update');
    if (fileRef.current) fileRef.current.value = '';
  };
  const close = () => { if (progress && !results) return; reset(); onClose(); };

  const pick = async (f) => {
    if (!f) return;
    reset();
    setBusy(true);
    try {
      setPreview(await window.AtlasApi.previewImport(kind, f));
    } catch (e) {
      setError(e.message || 'Could not read the file');
    } finally {
      setBusy(false);
    }
  };

  const rows = (preview && preview.rows) || [];
  const liveChanges = (r) => r.changes.filter((c) => !off.has(c.field));
  const offKeys = new Set(cfg.fields.filter(([f]) => off.has(f)).map(([, , k]) => k));
  const toApply = rows.filter((r) => r.status === 'update' && liveChanges(r).length).map((r) => {
    const set = Object.fromEntries(Object.entries(r.set || {}).filter(([k]) => !offKeys.has(k)));
    return kind === 'companies'
      ? { row: r.row, company_id: r.company_id, set, acquisition: off.has('is_acquired') ? null : r.acquisition }
      : { row: r.row, record_uuid: r.record_uuid, set };
  });
  const present = cfg.fields.filter(([f]) => rows.some((r) => r.changes.some((c) => c.field === f)));
  const countBy = (s) => rows.filter((r) => r.status === s).length;
  const problems = rows.filter((r) => r.status === 'not_found' || r.status === 'ambiguous' || (r.warnings || []).some((w) => !w.startsWith('Exits ')));
  const shown = filter === 'update' ? rows.filter((r) => r.status === 'update')
    : filter === 'problems' ? problems : rows;
  const exits = toApply.filter((r) => r.acquisition).length;
  const noun = (k) => (k === 1 ? one : many);

  const apply = async () => {
    const total = toApply.length;
    if (!total) return;
    const ok = await window.showConfirm({
      title: `Update ${total.toLocaleString()} ${noun(total)}?`,
      body: (exits ? `${exits} will be marked acquired: their live deals exit as "Company sold" and a note is logged on each. ` : '')
        + (pushDc ? 'Changes also go to DealCloud.' : 'DealCloud is left alone — the changes stay in Atlas.'),
      confirmLabel: 'Apply',
      danger: exits > 0,
    });
    if (!ok) return;
    const all = [];
    setProgress({ done: 0, total });
    for (let i = 0; i < total; i += IMPORT_CHUNK) {
      const chunk = toApply.slice(i, i + IMPORT_CHUNK);
      try {
        const res = await window.AtlasApi.applyImport(kind, { filename: preview.filename, push_to_dc: pushDc, rows: chunk });
        all.push(...res.results);
      } catch (e) {
        chunk.forEach((r) => all.push({ row: r.row, ok: false, error: e.message }));
      }
      setProgress({ done: Math.min(i + IMPORT_CHUNK, total), total });
    }
    setResults(all);
    const failed = all.filter((r) => !r.ok).length;
    pushToast({ type: failed ? 'warning' : 'success', title: `Updated ${(all.length - failed).toLocaleString()} ${noun(all.length - failed)}`,
                body: failed ? `${failed} row${failed === 1 ? '' : 's'} failed — download the results for why.` : undefined });
    if (onDone) onDone();
  };

  const downloadResults = () => {
    const byRow = Object.fromEntries((results || []).map((r) => [r.row, r]));
    const recs = rows.map((r) => {
      const res = byRow[r.row];
      return {
        row: r.row, input: r.input, id: r.record_id || '', record: recName(r) || '',
        matched_by: r.via || '', result: res ? (res.ok ? 'updated' : 'failed') : (STATUS_META[r.status] || {}).label || r.status,
        detail: res && !res.ok ? res.error : [r.note, ...(r.warnings || [])].filter(Boolean).join(' · '),
        changes: liveChanges(r).map((c) => `${c.label}: ${fmtImportValue(c.field, c.old)} → ${fmtImportValue(c.field, c.new)}`).join('; '),
      };
    });
    window.exportCsv(`import results — ${preview.filename}.csv`, [
      { key: 'row', label: 'Row' }, { key: 'input', label: 'Input' }, { key: 'id', label: 'ID' },
      { key: 'record', label: one[0].toUpperCase() + one.slice(1) }, { key: 'matched_by', label: 'Matched by' },
      { key: 'result', label: 'Result' }, { key: 'changes', label: 'Changes' }, { key: 'detail', label: 'Detail' },
    ], recs);
  };

  const running = progress && !results;
  const footer = preview && (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%' }}>
      {results ? (
        <>
          <span style={{ fontSize: 12, color: 'var(--text-2)' }}>
            {results.filter((r) => r.ok).length.toLocaleString()} updated
            {results.some((r) => !r.ok) ? ` · ${results.filter((r) => !r.ok).length} failed` : ''}
          </span>
          <div style={{ flex: 1 }} />
          <Btn size="sm" variant="outline" icon={<Icon.Download size={12} />} onClick={downloadResults}>Download results</Btn>
          <Btn size="sm" variant="accent" onClick={close}>Done</Btn>
        </>
      ) : (
        <>
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--text-2)', cursor: 'pointer' }}
            title="Off: the changes stay in Atlas. On: each change is also sent to DealCloud.">
            <input type="checkbox" checked={pushDc} disabled={running} onChange={(e) => setPushDc(e.target.checked)}
              style={{ accentColor: 'var(--accent)' }} />
            Also update DealCloud
          </label>
          <div style={{ flex: 1 }} />
          <Btn size="sm" variant="outline" disabled={running} onClick={close}>Cancel</Btn>
          <Btn size="sm" variant="accent" disabled={running || !toApply.length} onClick={apply}>
            {running ? `Applying… ${progress.done.toLocaleString()} / ${progress.total.toLocaleString()}`
              : `Update ${toApply.length.toLocaleString()} ${noun(toApply.length)}`}
          </Btn>
        </>
      )}
    </div>
  );

  return (
    <Drawer open={open} onClose={close} width={760} title={cfg.title} footer={footer}>
      <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 14 }}>
        {!preview && (
          <>
            <div style={{ fontSize: 12, color: 'var(--text-3)', lineHeight: 1.6 }}>Columns it reads: {cfg.columns}</div>
            <label style={{
              border: '1px dashed var(--border)', borderRadius: 8, padding: '28px 16px', textAlign: 'center',
              cursor: busy ? 'progress' : 'pointer', color: 'var(--text-2)', fontSize: 12.5,
            }}>
              <input ref={fileRef} type="file" accept=".csv,.xlsx,.xlsm,text/csv" style={{ display: 'none' }}
                disabled={busy} onChange={(e) => pick(e.target.files && e.target.files[0])} />
              {busy ? 'Reading the file…' : 'Choose a CSV or Excel file'}
            </label>
            {error && <div style={{ fontSize: 12, color: 'var(--red-fg, #b42318)' }}>{error}</div>}
          </>
        )}

        {preview && (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', fontSize: 12 }}>
              <span style={{ fontWeight: 600, color: 'var(--text)' }}>{preview.filename}</span>
              <span style={{ color: 'var(--text-3)' }}>· {rows.length.toLocaleString()} rows</span>
              {!results && !running && (
                <Btn size="xs" variant="outline" onClick={() => { reset(); setTimeout(() => fileRef.current && fileRef.current.click(), 0); }}>
                  Choose another file
                </Btn>
              )}
            </div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {['update', 'no_change', 'not_found', 'ambiguous'].filter((s) => countBy(s)).map((s) => (
                <Pill key={s} hue={STATUS_META[s].hue} sz="sm">{countBy(s).toLocaleString()} {STATUS_META[s].label}</Pill>
              ))}
            </div>
            {preview.ignored && preview.ignored.length > 0 && (
              <div style={{ fontSize: 11.5, color: 'var(--text-3)' }}>Ignored columns: {preview.ignored.join(', ')}</div>
            )}

            {present.length > 0 && !results && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-4)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                  Fields to update
                </div>
                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                  {present.map(([f, label]) => {
                    const n = rows.filter((r) => r.changes.some((c) => c.field === f)).length;
                    return (
                      <label key={f} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12, color: 'var(--text-2)', cursor: 'pointer' }}>
                        <input type="checkbox" checked={!off.has(f)} disabled={running} style={{ accentColor: 'var(--accent)' }}
                          onChange={() => setOff((s) => { const next = new Set(s); next.has(f) ? next.delete(f) : next.add(f); return next; })} />
                        {label} <span style={{ color: 'var(--text-4)' }} className="num">{n.toLocaleString()}</span>
                      </label>
                    );
                  })}
                </div>
              </div>
            )}

            <div style={{ display: 'flex', gap: 2, borderBottom: '1px solid var(--border)' }}>
              {[['update', `To update (${countBy('update').toLocaleString()})`],
                ['problems', `Needs a look (${problems.length.toLocaleString()})`],
                ['all', `All (${rows.length.toLocaleString()})`]].map(([k, l]) => (
                <button key={k} onClick={() => setFilter(k)} style={{
                  padding: '6px 10px', border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 12,
                  fontWeight: filter === k ? 600 : 500, color: filter === k ? 'var(--text)' : 'var(--text-3)',
                  borderBottom: filter === k ? '2px solid var(--text)' : '2px solid transparent', marginBottom: -1,
                }}>{l}</button>
              ))}
            </div>

            <div style={{ display: 'flex', flexDirection: 'column' }}>
              {shown.slice(0, 300).map((r) => {
                const res = results && results.find((x) => x.row === r.row);
                const changes = liveChanges(r);
                return (
                  <div key={r.row} style={{ padding: '8px 0', borderBottom: '1px solid var(--border-2, var(--border))', fontSize: 12 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                      <span style={{ color: 'var(--text-4)', minWidth: 42 }} className="num">row {r.row}</span>
                      {recUuid(r)
                        ? <a href={`#/${cfg.path}/${recUuid(r)}`} target="_blank" rel="noopener noreferrer"
                            style={{ fontWeight: 600, color: 'var(--text)', textDecoration: 'none' }}>{recName(r)}</a>
                        : <span style={{ fontWeight: 600, color: 'var(--text-2)' }}>{r.input || '(blank)'}</span>}
                      {r.record_sub && <span style={{ color: 'var(--text-3)' }}>· {r.record_sub}</span>}
                      {r.via && <span style={{ color: 'var(--text-4)' }}>by {r.via}</span>}
                      <Pill hue={(STATUS_META[r.status] || {}).hue || 'slate'} sz="sm">{(STATUS_META[r.status] || {}).label || r.status}</Pill>
                      {res && <Pill hue={res.ok ? 'green' : 'rose'} sz="sm">{res.ok ? 'updated' : 'failed'}</Pill>}
                    </div>
                    {changes.map((c) => (
                      <div key={c.field} style={{ marginLeft: 48, marginTop: 3, color: 'var(--text-2)' }}>
                        <span style={{ color: 'var(--text-3)' }}>{c.label}:</span>{' '}
                        <span style={{ textDecoration: 'line-through', color: 'var(--text-4)' }}>{fmtImportValue(c.field, c.old)}</span>
                        {' → '}<b>{fmtImportValue(c.field, c.new)}</b>
                      </div>
                    ))}
                    {[r.note, ...(r.warnings || []), res && !res.ok ? res.error : null].filter(Boolean).map((w, i) => (
                      <div key={i} style={{ marginLeft: 48, marginTop: 3, fontSize: 11.5,
                        color: w.startsWith('Exits ') ? 'var(--text-3)' : 'var(--amber-fg, #b54708)' }}>{w}</div>
                    ))}
                  </div>
                );
              })}
              {shown.length > 300 && (
                <div style={{ padding: '10px 0', fontSize: 11.5, color: 'var(--text-3)' }}>
                  Showing the first 300 of {shown.length.toLocaleString()} — every row is applied, and the results file lists them all.
                </div>
              )}
              {!shown.length && <div style={{ padding: '14px 0', fontSize: 12, color: 'var(--text-3)' }}>Nothing here.</div>}
            </div>
          </>
        )}
      </div>
    </Drawer>
  );
};

window.ImportDrawer = ImportDrawer;
window.CompanyImportDrawer = (props) => <ImportDrawer kind="companies" {...props} />;
